<script setup>
import { ref, computed } from 'vue'
import axios from '../axios.js'

const props = defineProps({
  visible: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['close'])

// Search state
const searchQuery = ref('')
const searchResults = ref([])
const isSearching = ref(false)
const searchError = ref(null)

// Selected player state
const selectedPlayer = ref(null)
const playerAliases = ref([])
const isLoadingAliases = ref(false)
const aliasesError = ref(null)

// Add/Edit alias state
const showAddAliasForm = ref(false)
const newAliasName = ref('')
const editingAliasId = ref(null)
const editingAliasName = ref('')
const isSavingAlias = ref(false)

// Search players function
const searchPlayers = async () => {
  if (!searchQuery.value || searchQuery.value.length < 2) {
    searchResults.value = []
    return
  }

  isSearching.value = true
  searchError.value = null

  try {
    const response = await axios.get(`/api/players/search/${searchQuery.value}`)
    searchResults.value = response.data
  } catch (error) {
    console.error('Error searching players:', error)
    searchError.value = error.response?.data?.message || 'Failed to search players'
    searchResults.value = []
  } finally {
    isSearching.value = false
  }
}

// Select player and load their aliases
const selectPlayer = async (player) => {
  selectedPlayer.value = player
  searchQuery.value = ''
  searchResults.value = []
  
  // If player already has aliases from search, use them
  if (player.aliases && Array.isArray(player.aliases)) {
    playerAliases.value = player.aliases
  } else {
    // Otherwise fetch them
    await loadPlayerAliases(player.player_id || player.id)
  }
}

// Load player aliases
const loadPlayerAliases = async (playerId) => {
  isLoadingAliases.value = true
  aliasesError.value = null

  try {
    const response = await axios.get(`/api/players/aliases/${playerId}`)
    const playerData = response.data
    
    // Update selected player with fresh data (including games_count)
    if (playerData) {
      selectedPlayer.value = playerData
      playerAliases.value = playerData.aliases || []
    } else {
      playerAliases.value = []
    }
  } catch (error) {
    console.error('Error loading aliases:', error)
    aliasesError.value = error.response?.data?.message || 'Failed to load aliases'
    playerAliases.value = []
  } finally {
    isLoadingAliases.value = false
  }
}

// Clear selection
const clearSelection = () => {
  selectedPlayer.value = null
  playerAliases.value = []
  showAddAliasForm.value = false
  newAliasName.value = ''
  editingAliasId.value = null
  editingAliasName.value = ''
}

// Show add alias form
const showAddForm = () => {
  showAddAliasForm.value = true
  newAliasName.value = ''
}

// Cancel add alias
const cancelAddAlias = () => {
  showAddAliasForm.value = false
  newAliasName.value = ''
}

// Save new alias
const saveNewAlias = async () => {
  if (!newAliasName.value.trim()) return

  isSavingAlias.value = true

  try {
    const response = await axios.post('/api/players/aliases', {
      player_id: selectedPlayer.value.player_id || selectedPlayer.value.id,
      player_name: newAliasName.value.trim()
    })

    // Add the new alias to the local array instead of refetching
    if (response.data.alias) {
      playerAliases.value.push(response.data.alias)
    }
    
    // Reset form
    showAddAliasForm.value = false
    newAliasName.value = ''
  } catch (error) {
    console.error('Error saving alias:', error)
    alert('Failed to save alias: ' + (error.response?.data?.message || error.message))
  } finally {
    isSavingAlias.value = false
  }
}

// Start editing alias
const startEditAlias = (alias) => {
  editingAliasId.value = alias.id
  editingAliasName.value = alias.player_name
}

// Cancel edit
const cancelEditAlias = () => {
  editingAliasId.value = null
  editingAliasName.value = ''
}

// Save edited alias
const saveEditedAlias = async (aliasId) => {
  if (!editingAliasName.value.trim()) return

  isSavingAlias.value = true

  try {
    const response = await axios.put(`/api/players/aliases/${aliasId}`, {
      player_name: editingAliasName.value.trim()
    })

    // Update the alias in the local array instead of refetching
    const index = playerAliases.value.findIndex(a => a.id === aliasId)
    if (index !== -1 && response.data.alias) {
      playerAliases.value[index] = response.data.alias
    }
    
    // Reset edit state
    editingAliasId.value = null
    editingAliasName.value = ''
  } catch (error) {
    console.error('Error updating alias:', error)
    alert('Failed to update alias: ' + (error.response?.data?.message || error.message))
  } finally {
    isSavingAlias.value = false
  }
}

// Delete confirmation state
const showDeleteConfirmation = ref(false)
const aliasToDelete = ref(null)

// Show delete confirmation
const confirmDeleteAlias = (alias) => {
  aliasToDelete.value = alias
  showDeleteConfirmation.value = true
}

// Cancel delete
const cancelDelete = () => {
  showDeleteConfirmation.value = false
  aliasToDelete.value = null
}

// Delete alias
const deleteAlias = async () => {
  if (!aliasToDelete.value) return

  try {
    await axios.delete(`/api/players/aliases/${aliasToDelete.value.id}`)

    // Remove the alias from the local array instead of refetching
    const index = playerAliases.value.findIndex(a => a.id === aliasToDelete.value.id)
    if (index !== -1) {
      playerAliases.value.splice(index, 1)
    }
    
    // Close confirmation modal
    showDeleteConfirmation.value = false
    aliasToDelete.value = null
  } catch (error) {
    console.error('Error deleting alias:', error)
    alert('Failed to delete alias: ' + (error.response?.data?.message || error.message))
  }
}

// Close modal
const closeModal = () => {
  clearSelection()
  searchQuery.value = ''
  searchResults.value = []
  emit('close')
}

// Handle click outside search results
const handleClickOutside = (event) => {
  if (!event.target.closest('.search-container')) {
    searchResults.value = []
  }
}
</script>

<template>
  <div 
    v-if="visible"
    class="fixed inset-0 bg-black/60 flex items-center justify-center z-[9999] p-2"
    @click.self="closeModal"
  >
    <div class="bg-white rounded-lg shadow-2xl w-[98vw] h-[96vh] overflow-hidden flex flex-col">
      <!-- Header -->
      <div class="bg-gradient-to-r from-blue-500 to-blue-600 px-6 py-4 flex items-center justify-between">
        <div class="flex items-center gap-3">
          <div class="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
            <i class="fas fa-user-tag text-white text-xl"></i>
          </div>
          <div>
            <h2 class="text-xl font-bold text-white">Player Alias Manager</h2>
            <p class="text-blue-100 text-sm">Search and manage player aliases</p>
          </div>
        </div>
        <button 
          @click="closeModal"
          class="text-white/80 hover:text-white hover:bg-white/10 p-2 rounded-lg transition-colors"
        >
          <i class="fas fa-times text-xl"></i>
        </button>
      </div>

      <!-- Content -->
      <div class="flex-1 overflow-hidden flex">
        <!-- Left Panel - Player Search -->
        <div class="w-[450px] border-r border-gray-200 flex flex-col bg-gray-50">
          <!-- Search Box -->
          <div class="p-4 border-b border-gray-200 bg-white">
            <div class="search-container relative">
              <input 
                v-model="searchQuery"
                @input="searchPlayers"
                type="text" 
                :placeholder="selectedPlayer ? 'Search to switch player...' : 'Search player by name...'"
                class="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
              <i class="fas fa-search absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
              
              <!-- Search indicator -->
              <div v-if="isSearching" class="absolute right-3 top-1/2 transform -translate-y-1/2">
                <i class="fas fa-spinner fa-spin text-blue-500"></i>
              </div>

              <!-- Search Results Dropdown -->
              <div 
                v-if="searchResults.length > 0"
                class="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg max-h-96 overflow-y-auto z-10"
              >
                <div 
                  v-for="player in searchResults" 
                  :key="player.player_id || player.id"
                  @click="selectPlayer(player)"
                  class="px-4 py-3 hover:bg-blue-50 cursor-pointer border-b border-gray-100 last:border-b-0 transition-colors"
                >
                  <div class="flex items-center gap-3">
                    <div 
                      class="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                      :class="player.match_type === 'alias' ? 'bg-purple-100' : 'bg-blue-100'"
                    >
                      <i 
                        class="text-sm"
                        :class="player.match_type === 'alias' ? 'fas fa-user-tag text-purple-600' : 'fas fa-user text-blue-600'"
                      ></i>
                    </div>
                    <div class="flex-1 min-w-0">
                      <div class="flex items-center gap-2 mb-1">
                        <span class="font-medium text-gray-900 truncate">{{ player.player_name }}</span>
                      </div>
                      <div class="flex items-center gap-2 flex-wrap">
                        <span class="text-xs text-gray-500">ID: {{ player.player_id || player.id }}</span>
                        
                        <!-- Match Type Badge -->
                        <span 
                          v-if="player.match_type === 'alias'" 
                          class="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded font-medium"
                          :title="`Matched via alias: ${player.matched_name}`"
                        >
                          <i class="fas fa-user-tag mr-1"></i>Alias Match
                        </span>
                        <span 
                          v-else
                          class="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded font-medium"
                        >
                          <i class="fas fa-user mr-1"></i>Direct Match
                        </span>
                        
                        <!-- Has Aliases Badge -->
                        <span 
                          v-if="player.aliases && player.aliases.length > 0" 
                          class="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded font-medium"
                          :title="`This player has ${player.aliases.length} alias${player.aliases.length !== 1 ? 'es' : ''}`"
                        >
                          <i class="fas fa-list mr-1"></i>{{ player.aliases.length }} alias{{ player.aliases.length !== 1 ? 'es' : '' }}
                        </span>
                        <span 
                          v-else
                          class="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded"
                        >
                          No aliases
                        </span>
                      </div>
                      <div v-if="player.match_type === 'alias' && player.matched_name" class="text-xs text-purple-600 mt-1 italic">
                        Matched: "{{ player.matched_name }}"
                      </div>
                      <!-- List of aliases -->
                      <div v-if="player.aliases && player.aliases.length > 0" class="text-xs text-gray-500 mt-1.5">
                        <span class="font-medium text-gray-600">Aliases:</span>
                        <span class="ml-1">
                          <span 
                            v-for="(alias, idx) in player.aliases" 
                            :key="alias.id"
                          >
                            <span 
                              class="inline-flex items-center px-1.5 py-0.5 rounded"
                              :class="alias.player_name === player.matched_name ? 'bg-purple-50 text-purple-700 font-medium' : 'text-gray-600'"
                            >
                              {{ alias.player_name }}
                            </span>{{ idx < player.aliases.length - 1 ? ', ' : '' }}
                          </span>
                        </span>
                      </div>
                    </div>
                    <i class="fas fa-chevron-right text-gray-400 text-xs flex-shrink-0"></i>
                  </div>
                </div>
              </div>
            </div>

            <!-- Search Error -->
            <div v-if="searchError" class="mt-2 text-red-500 text-sm flex items-center gap-2">
              <i class="fas fa-exclamation-circle"></i>
              {{ searchError }}
            </div>
          </div>

          <!-- Selected Player Info -->
          <div v-if="selectedPlayer" class="p-4 bg-white border-b border-gray-200">
            <div class="flex items-center justify-between mb-3">
              <div class="flex items-center gap-2">
                <span class="text-xs font-medium text-gray-500 uppercase">Selected Player</span>
                <span v-if="playerAliases.length > 0" class="px-2 py-0.5 bg-blue-500 text-white text-xs rounded-full">
                  {{ playerAliases.length }}
                </span>
              </div>
              <button 
                @click="clearSelection"
                class="text-gray-400 hover:text-red-500 text-xs"
              >
                <i class="fas fa-times mr-1"></i>
                Clear
              </button>
            </div>
            <div class="bg-blue-50 rounded-lg p-3 border border-blue-200">
              <div class="flex items-center gap-3">
                <div class="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center">
                  <i class="fas fa-user text-white"></i>
                </div>
                <div class="flex-1">
                  <div class="font-semibold text-gray-900">{{ selectedPlayer.player_name }}</div>
                  <div class="text-xs text-gray-600">Player ID: {{ selectedPlayer.player_id || selectedPlayer.id }}</div>
                  <div class="text-xs text-gray-600 mt-1 flex items-center gap-3">
                    <span v-if="selectedPlayer.games_count !== undefined" class="text-green-600">
                      <i class="fas fa-basketball-ball mr-1"></i>{{ selectedPlayer.games_count }} game{{ selectedPlayer.games_count !== 1 ? 's' : '' }}
                    </span>
                    <span v-if="selectedPlayer.team">
                      <i class="fas fa-shield-alt mr-1"></i>{{ selectedPlayer.team }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Instructions when no player selected -->
          <div v-else class="flex-1 flex items-center justify-center p-6">
            <div class="text-center text-gray-400">
              <i class="fas fa-search text-4xl mb-3"></i>
              <p class="text-sm">Search for a player to manage their aliases</p>
            </div>
          </div>
        </div>

        <!-- Right Panel - Aliases Management -->
        <div class="flex-1 flex flex-col overflow-hidden">
          <!-- No player selected state -->
          <div v-if="!selectedPlayer" class="flex-1 flex items-center justify-center p-6">
            <div class="text-center text-gray-400">
              <i class="fas fa-user-tag text-5xl mb-4"></i>
              <p class="text-lg font-medium text-gray-600 mb-2">No Player Selected</p>
              <p class="text-sm">Select a player from the search results to view and manage their aliases</p>
            </div>
          </div>

          <!-- Player aliases content -->
          <div v-else class="flex-1 flex flex-col overflow-hidden">
            <!-- Header with Add button -->
            <div class="px-6 py-4 border-b border-gray-200 bg-white flex items-center justify-between">
              <div>
                <h3 class="text-lg font-semibold text-gray-900">Player Aliases</h3>
                <p class="text-sm text-gray-600">Manage alternative names for this player</p>
              </div>
              <button 
                @click="showAddForm"
                :disabled="showAddAliasForm"
                class="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center gap-2 transition-colors"
              >
                <i class="fas fa-plus"></i>
                Add Alias
              </button>
            </div>

            <!-- Aliases list -->
            <div class="flex-1 overflow-y-auto p-6">
              <!-- Loading state -->
              <div v-if="isLoadingAliases" class="flex items-center justify-center py-12">
                <div class="text-center">
                  <i class="fas fa-spinner fa-spin text-blue-500 text-3xl mb-3"></i>
                  <p class="text-gray-600">Loading aliases...</p>
                </div>
              </div>

              <!-- Error state -->
              <div v-else-if="aliasesError" class="flex items-center justify-center py-12">
                <div class="text-center text-red-500">
                  <i class="fas fa-exclamation-triangle text-3xl mb-3"></i>
                  <p class="mb-3">{{ aliasesError }}</p>
                  <button 
                    @click="loadPlayerAliases(selectedPlayer.player_id || selectedPlayer.id)"
                    class="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
                  >
                    Retry
                  </button>
                </div>
              </div>

              <!-- Aliases content -->
              <div v-else class="space-y-3">
                <!-- Add alias form -->
                <div v-if="showAddAliasForm" class="bg-green-50 border-2 border-green-500 rounded-lg p-4 mb-4">
                  <div class="flex items-center gap-2 mb-3">
                    <i class="fas fa-plus-circle text-green-600"></i>
                    <span class="font-semibold text-green-900">Add New Alias</span>
                  </div>
                  <div class="flex gap-2">
                    <input 
                      v-model="newAliasName"
                      @keyup.enter="saveNewAlias"
                      @keyup.esc="cancelAddAlias"
                      type="text" 
                      placeholder="Enter alias name..."
                      class="flex-1 px-3 py-2 border border-green-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                      :disabled="isSavingAlias"
                    >
                    <button 
                      @click="saveNewAlias"
                      :disabled="!newAliasName.trim() || isSavingAlias"
                      class="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                    >
                      <i v-if="isSavingAlias" class="fas fa-spinner fa-spin"></i>
                      <i v-else class="fas fa-check"></i>
                    </button>
                    <button 
                      @click="cancelAddAlias"
                      :disabled="isSavingAlias"
                      class="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                    >
                      <i class="fas fa-times"></i>
                    </button>
                  </div>
                </div>

                <!-- Empty state -->
                <div v-if="playerAliases.length === 0 && !showAddAliasForm" class="text-center py-12">
                  <i class="fas fa-inbox text-gray-300 text-4xl mb-3"></i>
                  <p class="text-gray-600 mb-4">No aliases found for this player</p>
                  <button 
                    @click="showAddForm"
                    class="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
                  >
                    <i class="fas fa-plus mr-2"></i>
                    Add First Alias
                  </button>
                </div>

                <!-- Aliases list -->
                <div v-for="alias in playerAliases" :key="alias.id" class="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                  <!-- View mode -->
                  <div v-if="editingAliasId !== alias.id" class="flex items-center justify-between">
                    <div class="flex items-center gap-3 flex-1">
                      <div class="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                        <i class="fas fa-user-tag text-blue-600"></i>
                      </div>
                      <div class="flex-1">
                        <div class="font-semibold text-gray-900">{{ alias.player_name }}</div>
                        <div class="text-xs text-gray-500 flex items-center gap-3 mt-1">
                          <span><i class="fas fa-hashtag mr-1"></i>ID: {{ alias.id }}</span>
                          <span><i class="fas fa-user mr-1"></i>Player ID: {{ alias.player_id }}</span>
                          <span><i class="far fa-clock mr-1"></i>{{ alias.created_at }}</span>
                        </div>
                      </div>
                    </div>
                    <div class="flex items-center gap-2">
                      <button 
                        @click="startEditAlias(alias)"
                        class="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        title="Edit"
                      >
                        <i class="fas fa-edit"></i>
                      </button>
                      <button 
                        @click="confirmDeleteAlias(alias)"
                        class="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Delete"
                      >
                        <i class="fas fa-trash"></i>
                      </button>
                    </div>
                  </div>

                  <!-- Edit mode -->
                  <div v-else class="flex items-center gap-2">
                    <input 
                      v-model="editingAliasName"
                      @keyup.enter="saveEditedAlias(alias.id)"
                      @keyup.esc="cancelEditAlias"
                      type="text" 
                      class="flex-1 px-3 py-2 border border-blue-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      :disabled="isSavingAlias"
                    >
                    <button 
                      @click="saveEditedAlias(alias.id)"
                      :disabled="!editingAliasName.trim() || isSavingAlias"
                      class="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                    >
                      <i v-if="isSavingAlias" class="fas fa-spinner fa-spin"></i>
                      <i v-else class="fas fa-check"></i>
                    </button>
                    <button 
                      @click="cancelEditAlias"
                      :disabled="isSavingAlias"
                      class="px-3 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 disabled:cursor-not-allowed"
                    >
                      <i class="fas fa-times"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <div 
      v-if="showDeleteConfirmation"
      class="fixed inset-0 bg-black/70 flex items-center justify-center z-[10000]"
      @click.self="cancelDelete"
    >
      <div class="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 overflow-hidden" @click.stop>
        <!-- Header -->
        <div class="bg-red-500 px-6 py-4">
          <div class="flex items-center gap-3 text-white">
            <div class="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
              <i class="fas fa-exclamation-triangle text-xl"></i>
            </div>
            <h3 class="text-xl font-bold">Delete Alias</h3>
          </div>
        </div>
        
        <!-- Content -->
        <div class="p-6">
          <p class="text-gray-700 mb-2">Are you sure you want to delete this alias?</p>
          <div v-if="aliasToDelete" class="bg-red-50 border border-red-200 rounded-lg p-3 mt-3">
            <div class="flex items-center gap-2">
              <i class="fas fa-user-tag text-red-600"></i>
              <span class="font-semibold text-red-900">{{ aliasToDelete.player_name }}</span>
            </div>
            <div class="text-xs text-red-600 mt-1">ID: {{ aliasToDelete.id }}</div>
          </div>
          <p class="text-sm text-red-600 mt-3 font-medium">
            <i class="fas fa-info-circle mr-1"></i>
            This action cannot be undone.
          </p>
        </div>
        
        <!-- Actions -->
        <div class="bg-gray-50 px-6 py-4 flex gap-3">
          <button
            @click="cancelDelete"
            class="flex-1 px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
          >
            <i class="fas fa-times mr-2"></i>
            Cancel
          </button>
          <button
            @click="deleteAlias"
            class="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium"
          >
            <i class="fas fa-trash mr-2"></i>
            Delete Alias
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Custom scrollbar */
.overflow-y-auto {
  scrollbar-width: thin;
  scrollbar-color: rgba(156, 163, 175, 0.5) transparent;
}

.overflow-y-auto::-webkit-scrollbar {
  width: 6px;
}

.overflow-y-auto::-webkit-scrollbar-track {
  background: transparent;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background-color: rgba(156, 163, 175, 0.5);
  border-radius: 3px;
}

.overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background-color: rgba(156, 163, 175, 0.7);
}
</style>

